import React from 'react';
import { Box, Link } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BRAND_ROUTES from 'Sp/Angular/Routes/Client/Brand';
import Route from 'Sp/Route';
import { useTranslate } from 'ts/common/hooks';
import { IBrandHomepage } from '../types';

interface INavProps {
    brandData: IBrandHomepage;
}

interface NavItem {
    name: string;
    route: string;
    skipTranslation?: boolean;
}

const NavContainer = styled(Box)(
    ({ theme }) => `
        width: 100%;
        ${theme.responsive.mediumScreenDown`
            width: calc(100% - 32px);
        `}
        max-width: 1200px;
        text-align: center;
        border-bottom: 1px solid ${theme.brand.colors.theme.secondary.medium};
        padding: 10px 0;
        margin: 0 auto 24px auto;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        gap: 20px;
    `
);
const NavLink = styled(Link)(
    ({ theme }) => `
        color: ${theme.brand.colors.user.primary};
        text-decoration: none;
        text-underline-offset: 12px;
        text-transform: uppercase;
        font-family: ${theme.brand.fonts.primary};
        letter-spacing: 0.07em;
        white-space: nowrap;
        flex-shrink: 0;

        &:active, &:hover, &:link, &:visited {
            color: ${theme.brand.colors.user.primary};
        }

        &:hover, &:focus, &.selected {
            text-decoration: underline;
            text-decoration-thickness: 2px;
            font-weight: 700;
            box-shadow: none;
        }
    `
);

const Nav: React.FC<INavProps> = ({ brandData }) => {
    const t = useTranslate();
    const params = useParams();
    const location = useLocation();
    const categoryRoute = Route.get(BRAND_ROUTES.CATEGORY, { categoryId: params.categoryId });
    const aboutRoute = Route.get(BRAND_ROUTES.ABOUT);
    const bookingRoute = Route.get(BRAND_ROUTES.BOOKING);
    const contactRoute = Route.get(BRAND_ROUTES.CONTACT);
    const homeRoute = Route.get(BRAND_ROUTES.HOME);
    const navigate = useNavigate();

    const selected = (routeToMatch: string) => {
        return location.pathname === routeToMatch;
    };

    const getBookingNavName = () => {
        if (brandData.bookingNavLinkText) {
            return {
                name: brandData.bookingNavLinkText,
                skipTranslation: true
            };
        }

        return {
            name: 'booking.brandHomepage.navLinkText',
            skipTranslation: false
        };
    };

    const navItems: NavItem[] = [
        {
            name: 'home',
            route: homeRoute
        }
    ];

    if (brandData.enableAboutPage) {
        navItems.push({
            name: 'about',
            route: aboutRoute
        });
    }

    if (brandData.enableContactForm) {
        navItems.push({
            name: 'contact',
            route: contactRoute
        });
    }

    if (brandData.showBookingPage) {
        const bookingNav = getBookingNavName();
        navItems.push({
            ...bookingNav,
            route: bookingRoute
        });
    }

    return (
        <NavContainer data-testid="nav-container">
            {navItems.map(({ route, name, skipTranslation = false }) => (
                <NavLink
                    key={name}
                    data-testid={`${name}-link`}
                    href={route}
                    onClick={(event) => {
                        event.preventDefault();
                        navigate(route);
                    }}
                    className={
                        selected(route) || (selected(categoryRoute) && route === homeRoute)
                            ? 'selected'
                            : ''
                    }
                >
                    {skipTranslation ? name : t(name)}
                </NavLink>
            ))}
        </NavContainer>
    );
};

export default Nav;
